import { Tag } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type {
  IHotelWithDetails,
  IMergeExperienceItem,
  IMergePackageItem,
  IMergeRoomItem,
} from 'app/typings/migrationTool/HotelWithDetails';

import { getDifferences, getTitle } from '../../utils';
import { Action } from '../Action/Action';
import {
  HotelServiceCard,
  SelectActionEnum,
} from '../HotelServiceCard/HotelServiceCard';
import { Arrow } from '../Icons/Arrow';
import { LineArrow } from '../Icons/LineArrow';
import { LongArrow } from '../Icons/LongArrow';
import { ShortArrow } from '../Icons/ShortArrow';

import './MergeAction.scss';

export type ActionType = {
  label: string;
  value: number;
  isPublished: boolean;
  disabled: boolean;
};

export type MergeActionChildService =
  | (IMergeRoomItem & { bedsList?: string[] })
  | IMergeExperienceItem
  | IMergePackageItem;

type MergeActionProps<
  T extends MergeActionChildService = MergeActionChildService,
> = {
  childService: T;
  parentService: IHotelWithDetails;
  selectedParentItem?: MergeActionChildService;
  selectAction?: SelectActionEnum;
  selectTargetId?: number;
  actionList?: ActionType[];
  isReview?: boolean;
  onSelectAction?: (action: SelectActionEnum, targetId?: number) => void;
  entityLabel: 'Room' | 'Experience' | 'Package' | 'Hotel';
  isFirstActionOfList?: boolean;
  childHotel: string;
};

export const MergeAction = <T extends MergeActionChildService>({
  childService,
  parentService,
  selectAction,
  actionList = [],
  isReview = false,
  selectedParentItem,
  selectTargetId,
  onSelectAction,
  entityLabel,
  isFirstActionOfList = false,
  childHotel,
}: MergeActionProps<T>) => {
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: roomCategories } = useRoomCategories();
  const { currencyFormatter } = useContext(CurrencyContext);

  const differingFields = getDifferences(childService, selectedParentItem);

  const renderAction = () => (
    <>
      <div>
        {isFirstActionOfList && <div className="action-empty"></div>}
        <Arrow />
      </div>
      <div className="action-content">
        {isFirstActionOfList && <div className="action-empty"></div>}
        <Action
          actionList={actionList}
          entityLabel={entityLabel}
          differingFields={differingFields}
          value={selectTargetId ? selectTargetId : selectAction}
          onChange={(value) => {
            if (!onSelectAction) {
              return;
            }

            if (value === 'CREATE_NEW') {
              onSelectAction(SelectActionEnum.CREATE_NEW);
            } else if (value === 'DO_NOT_MERGE') {
              onSelectAction(SelectActionEnum.DO_NOT_MERGE);
            } else if (typeof value === 'number') {
              onSelectAction(SelectActionEnum.MERGE, value);
            }
          }}
          hasBooking={childService.hasBooking}
        />
      </div>

      <div>
        {isFirstActionOfList && <div className="action-empty"></div>}
        <Arrow />
      </div>
    </>
  );
  const renderReview = (tagValue?: { text: string; color: string }) =>
    tagValue ? (
      <div className="arrow-group">
        <LineArrow />
        <Tag color={tagValue.color}>{tagValue.text}</Tag>
        <ShortArrow />
      </div>
    ) : (
      <LongArrow />
    );

  const retrieveWordingAction = () => {
    switch (selectAction) {
      case SelectActionEnum.DO_NOT_MERGE:
        return { text: 'Do not migrate', color: 'orange' };
      case SelectActionEnum.CREATE_NEW:
        return { text: 'Create in parent hotel', color: 'blue' };
      default:
        return { text: 'Merge with', color: 'green' };
    }
  };

  const getRoomCategory = (categoryId: number) =>
    roomCategories?.find((rc) => rc.id === categoryId)?.name;

  const getDescription = (service?: MergeActionChildService) => {
    if (!service) {
      return undefined;
    }

    switch (entityLabel) {
      case 'Room':
        return 'categoryId' in service
          ? getRoomCategory(service.categoryId)
          : undefined;
      case 'Experience':
        const price = 'price' in service ? service.price : 0;
        const priceDescription =
          'priceDescription' in service ? service.priceDescription : undefined;

        return `${currencyFormatter(price)} ${priceDescription ?? ''}`;
      case 'Package':
        return 'coverLabel' in service ? service.coverLabel : undefined;

      case 'Hotel':
        return 'aliasForHotel' in service ? service.aliasForHotel : undefined;
      default:
        return undefined;
    }
  };

  return (
    <div className="merge-action">
      <HotelServiceCard
        title={getTitle(entityLabel, childService, experienceCategories) ?? ''}
        description={getDescription(childService)}
        isPublished={childService.isPublished}
        bedsList={
          'bedsList' in childService ? childService.bedsList : undefined
        }
        picture={childService.coverPicture}
        isChild
        redirectUrl={`/${entityLabel.toLowerCase()}s/${childService.id}/edit`}
        hotelName={isFirstActionOfList ? childHotel : undefined}
      />

      {isReview ? renderReview(retrieveWordingAction()) : renderAction()}

      <HotelServiceCard
        title={
          getTitle(entityLabel, selectedParentItem, experienceCategories) ??
          parentService.hotel.name
        }
        description={getDescription(selectedParentItem)}
        isPublished={
          selectedParentItem && 'isPublished' in selectedParentItem
            ? (selectedParentItem.isPublished ?? false)
            : (parentService.packages[0]?.isPublished ?? false)
        }
        bedsList={
          selectedParentItem && 'bedsList' in selectedParentItem
            ? selectedParentItem.bedsList
            : undefined
        }
        picture={selectedParentItem?.coverPicture ?? childService.coverPicture}
        selectAction={selectAction}
        hotelName={isFirstActionOfList ? parentService.hotel.name : undefined}
        redirectUrl={
          selectedParentItem
            ? `/${entityLabel.toLowerCase()}s/${selectedParentItem.id}/edit`
            : undefined
        }
        entity={entityLabel}
      />
    </div>
  );
};
